import React, { RefObject, forwardRef, useCallback } from 'react';

import { MenuItem } from '@mui/material';
import { startCase } from 'lodash';

import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { CustomizedDropdownMenu } from 'components/common/CustomizedDropdownMenu/CustomizedDropdownMenu';
import { CustomizedTooltip } from 'components/common/CustomizedTooltip/CustomizedTooltip';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportCsv } from 'components/common/ExportDropdown/ExportCsv/exportCsv';
import {
    convertComponentToImage,
    convertComponentsToPPTX,
    convertTableToPPTX,
} from 'components/common/ExportDropdown/exportDropdown.utils';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { WidgetButton } from 'components/Dashboards/Reporting/_common/widgetButtons/WidgetButton';

interface Props extends React.PropsWithChildren {
    title?: string;
    subTitle: string;
    data?: any[];
    pptxData?: any;
    tableInstance?: any;
    handleAPICsvExport?: ({ isCompact }: { isCompact?: boolean }) => void;
    handleAPICsvExportCompact?: () => void;
    handleAPIXlsExport?: () => void;
    handleAPIExcelExport?: () => void;
    handleExrExport?: () => void;
    handleMppExport?: () => void;
    activeTabTitle?: string;
    exportTargetRef?: RefObject<HTMLDivElement>;
    customTriggerElement?: React.ReactNode;
}
export const ExportDropdown = forwardRef(
    (
        {
            title = '',
            data,
            pptxData,
            tableInstance,
            subTitle,
            handleAPICsvExport,
            handleAPICsvExportCompact,
            handleAPIXlsExport,
            handleAPIExcelExport,
            handleExrExport,
            activeTabTitle,
            children,
            exportTargetRef,
            customTriggerElement,
        }: Props,
        boundaryRef: React.ForwardedRef<HTMLDivElement>,
    ) => {
        const { sendEventWithDimensions } = useEventWithDimensions();
        const { logo } = useUserHook();
        const elementRef = exportTargetRef ?? (boundaryRef as RefObject<HTMLDivElement>);
        const handleExportImage = useCallback(() => {
            sendEventWithDimensions({
                category: 'Export',
                action: 'Image',
                label: `${title} ${subTitle}`,
            });
            convertComponentToImage({
                elementRef: elementRef,
                widgetTitle: title,
                subTitle,
            });
        }, [boundaryRef, title, subTitle]);

        const handleExportPptx = useCallback(() => {
            sendEventWithDimensions({
                category: 'Export',
                action: 'PPTX',
                label: `${title} ${subTitle}`,
            });
            if (pptxData) {
                return convertTableToPPTX({
                    elementRef: boundaryRef,
                    widgetTitle: title,
                    logoSRC: logo,
                    linkToWidget: window.location.href,
                    pptxData,
                    subTitle,
                });
            } else {
                if (elementRef) {
                    return convertComponentsToPPTX({
                        elementRef: {
                            current: [{ el: elementRef.current, order: 0, subTitle, title }],
                        },
                        widgetTitle: title,
                        logoSRC: logo,
                        linkToWidget: window.location.href,
                    });
                }
            }
        }, [boundaryRef, title, subTitle, logo, pptxData]);
        const csvFileTitle = activeTabTitle
            ? `${title}_${startCase(activeTabTitle)}_${subTitle}`
            : `${title}_${subTitle}`;
        return (
            <CustomizedDropdownMenu triggerElement={<Trigger customTriggerElement={customTriggerElement} />}>
                <MenuItem onClick={handleExportImage}>Export to IMAGE</MenuItem>
                <MenuItem onClick={handleExportPptx}>Export to PPTX</MenuItem>
                {data && data.length > 0 && (
                    <MenuItem>
                        <ExportCsv tableInstance={tableInstance} fileTitle={csvFileTitle} />
                    </MenuItem>
                )}
                {handleAPICsvExport && (
                    <MenuItem onClick={handleAPICsvExport}>
                        Export to CSV{`${handleAPICsvExportCompact ? ' (Full)' : ''}`}
                    </MenuItem>
                )}
                {handleAPICsvExportCompact && (
                    <MenuItem onClick={handleAPICsvExportCompact}>Export to CSV (Visible columns only)</MenuItem>
                )}
                {handleAPIXlsExport && <MenuItem onClick={handleAPIXlsExport}>Export to P6 Import Format</MenuItem>}
                {handleAPIExcelExport && <MenuItem onClick={handleAPIExcelExport}>Export to Excel</MenuItem>}
                {handleExrExport && <MenuItem onClick={handleExrExport}>Export to XER</MenuItem>}
                {/*{handleMppExport && <div onClick={handleMppExport}>Export to MS-Project XML</div>}*/}
                {children}
            </CustomizedDropdownMenu>
        );
    },
);

const Trigger = ({ customTriggerElement }) => (
    <CustomizedTooltip
        tooltipPosition={TooltipPosition.Top}
        tooltipContent={`Export`}
        triggerElement={
            customTriggerElement ? (
                customTriggerElement
            ) : (
                <div style={{ position: 'relative', top: '1px' }}>
                    <WidgetButton icon={'download'} />
                </div>
            )
        }
    />
);
